import {
  CalendarConfigurationDto,
  DayOfWeek,
  PatchVisitDto,
  NoteDto,
  QueryFactory,
  ResourceDto,
  ResourceType,
  VisitDto,
  VisitListDto,
  AbsenceType,
} from '@lmt/shared-types';
import { Resources } from '@lmt/unicorn-shared/app/components/calendar/calendar.types';
import {
  CalendarCellDropTarget,
  DroppedItemType,
  HolidaysAndAnonimized,
} from '@lmt/unicorn-shared/app/components/calendar/devextreme/common';
import { useItemDroppedToCalendarCell } from '@lmt/unicorn-shared/app/components/calendar/devextreme/components/useItemDroppedHandler';
import { NotePopup } from '@lmt/unicorn-shared/app/components/calendar/notes/popup/NotePopup';
import { AppPopper } from '@lmt/unicorn-shared/app/components/common/ui-kit/tooltip/AppPopper';
import { isAnyObjectInDataSourceSuitFilter } from '@lmt/unicorn-shared/app/components/documents/components/data-source-helpers';
import { useGlobalForms } from '@lmt/unicorn-shared/app/components/global-forms/useGlobalForms';
import { useActiveVisitTypes } from '@lmt/unicorn-shared/app/features/dictionaries/selectors';
import { localFormat } from '@lmt/unicorn-shared/app/helpers/date-helper';
import HolidayIcon from '@lmt/unicorn-shared/app/icons/holiday.svg?react';
import CheckedIcon from '@lmt/unicorn-shared/app/icons/checked-in-circle.svg?react';
import {
  AppointmentItem,
  useAppointmentResourceDetails,
  useVisitDeviceMeasurements,
} from '@lmt/unicorn-shared/src/app/components/calendar/devextreme/resource-view/ResourceViewAppointment';
import clsx from 'clsx';
import {
  addDays,
  addMilliseconds,
  differenceInMilliseconds,
  endOfDay,
  endOfMonth,
  format,
  getISOWeek,
  isSameDay,
  startOfDay,
  startOfMonth,
  endOfWeek,
} from 'date-fns';
import { bindHover, bindPopper } from 'material-ui-popup-state';
import { usePopupState } from 'material-ui-popup-state/hooks';
import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  getBGColor,
  getFontColor,
} from '../../color-settings/components/color-parser';
import { useFirstDayOfWeek } from '../../common';
import { AppointmentPopup } from '../components/AppointmentPopup';
import { CalendarDateCellDropHandler } from '../components/DragAndDropPatient';
import devExpressSchedulerStyles from '../DevExpressScheduler.module.scss';
import {
  convertDaysToIndex,
  correctAppointmentsAccordingToExcludedDays,
  useExcludedDaysInfo,
} from './excluded-days-helper';

import styles from './ResourceViewScheduler.module.scss';
import calendarStyles from '../components/Calendar.module.scss';
import {
  AppointmentInfo,
  MonthAppointmentPopup,
} from './MonthAppointmentPopup';
import {
  AppointmentClickEvent,
  AppointmentDblClickEvent,
  AppointmentRenderedEvent,
  AppointmentUpdatingEvent,
  DXS,
  useDxScheduler,
  SchedulerRef,
  Appointment,
  ViewType,
  Cancelable,
} from '@lmt/unicorn-shared/app/common/devexpress/Scheduler/dx-scheduler';
import { DXDS } from '@lmt/unicorn-shared/app/common/devexpress/dx-data-source';
import i18next from 'i18next';
import { useWindowSize } from 'react-use';
import { useDebounce } from 'use-debounce';
import { useEventCallback } from '@mui/material';
import { useUniSelector } from '@lmt/unicorn-shared/app/store/types';
import { getSettings } from '@lmt/unicorn-shared/app/features/settings/selectors';
import { NoteIconComponent } from './NoteIconComponent';
import { mergeRefs } from 'react-merge-refs';
import { getBridge } from 'leo-manager/utils/bridge';
import { WorkstationSettingsData } from '../../../workstation-settings/components/types';
import { useLocation } from 'react-router';
import { EnableDebounceContext } from 'devextreme-react';
export type SchedulerProps = {
  appointmentComponent: React.ComponentType<any>;
  appointments: Appointment[];
  // key is date in 'yyyy-MM-dd' format
  holidays: Record<string, HolidaysAndAnonimized[]>;
  places: ResourceDto[];
  specialConditions: ResourceDto[];
  doctors: ResourceDto[];
  currentDate: Date;
  configuration: CalendarConfigurationDto;
  notes: Record<string, NoteDto[]>;
  updateVisit: (
    visitId: number,
    newVisitData: PatchVisitDto,
    oldVisitData: Appointment,
    event: AppointmentUpdatingEvent,
  ) => void;
  getOrCreateAppointmentModel: (
    visitId: number,
  ) => Promise<Appointment | undefined>;
  replaceAppointmentModel: (appointment: Appointment) => void;
  /*
   * Only applicable if `calendarType` is `Week`
   */
  excludedDays?: Array<DayOfWeek>;
  currentView: ViewType;
  className?: string;
  schedulerRef?: React.RefObject<SchedulerRef>;
};

const editingSettings = {
  allowDragging: true,
  allowResizing: false,
  allowDeleting: false,
};
const groups = ['resources'];

export type NotePopupProps = {
  date: Date;
  notes: NoteDto[];
};

export type AppointmentPopupProps = {
  date: Date;
  appointments: AppointmentInfo[];
};

const defaultVisitDurationInSeconds = 3600;
const maxVisitsPerMonthCell = parseInt(
  window.localStorage.getItem('maxVisitsPerCell') ?? '6',
);
const maxVisitsPerTimelineMonthCell = 4;

export const ResourceViewScheduler: FC<SchedulerProps> = React.memo(
  function ResourceViewScheduler(props) {
    const {
      appointments: appointmentsProps,
      updateVisit,
      getOrCreateAppointmentModel,
      configuration,
      places,
      currentView,
      specialConditions,
      doctors,
      currentDate: currentDateProps,
      replaceAppointmentModel,
      excludedDays,
      notes,
      ...rest
    } = props;

    useDxScheduler();
    const location = useLocation();
    const schedulerRef = useRef<SchedulerRef>(null);
    const forms = useGlobalForms();
    const colorSettingsQuery =
      QueryFactory.AppointmentColorSettingsQuery.useGetQuery();

    const resourcesQuery = QueryFactory.ResourceQuery.useGetResourcesQuery({
      includeDeleted: false,
    });
    const resources = resourcesQuery.data?.data;

    const visitTypes = useActiveVisitTypes();

    const popupState = usePopupState({
      variant: 'popper',
      popupId: 'calendar-note',
    });

    const popupVisitState = usePopupState({
      variant: 'popper',
      popupId: 'calendar-visit',
    });
    const [popupNotes, setPopupNotes] = useState<NotePopupProps | undefined>(
      undefined,
    );
    const [popupAppointments, setPopupAppointments] = useState<
      AppointmentPopupProps | undefined
    >(undefined);
    const bindHoverRef = bindHover(popupState);
    const bindHoverVisitRef = bindHover(popupVisitState);

    const isNightTheme = useUniSelector(getSettings).isNightTheme;

    const [workstationSettings, setWorkstationSettings] =
      useState<WorkstationSettingsData | null>(null);

    const firstDayOfWeekIndexConfiguration = useFirstDayOfWeek();
    const { currentDate, firstDayOfWeekIndex, hasExcludedDays } =
      useExcludedDaysInfo(
        currentView,
        excludedDays,
        currentDateProps,
        firstDayOfWeekIndexConfiguration,
      );

    useEffect(() => {
      const bridge = getBridge();
      bridge?.getWorkstationSettings()?.then((data) => {
        setWorkstationSettings(data);
      });
    }, [location]);

    /**
     * List of locations, devices (resources of type "Place" and "Special conditions") and doctors displayed in vertical axis of resource view
     */
    const shownResources: Resources = useMemo(() => {
      const resources: Resources = {
        places: [],
        specialConditions: [],
        doctors: [],
      };
      if (!configuration.isResourceViewCalendar) {
        return resources;
      }

      if (configuration.hasFilterByPlaces) {
        resources.places = places;
      }
      if (configuration.hasFilterBySpecialConditions) {
        resources.specialConditions = specialConditions;
      }
      if (configuration.hasFilterByDoctors) {
        resources.doctors = doctors;
      }
      return resources;
    }, [configuration, places, specialConditions, doctors]);

    const shownResourcesList = useMemo(() => {
      return [
        ...shownResources.places,
        ...shownResources.specialConditions,
        ...shownResources.doctors,
      ].map((item) => ({
        id: item.id,
        text: item.name,
        additionalText: undefined,
      }));
    }, [shownResources]);

    const getResourcesToCreateNewVisit = useCallback(
      (selectedCellResourceId: number) => {
        const selectedCellResource = [
          ...places,
          ...specialConditions,
          ...doctors,
        ].find((item) => item.id === selectedCellResourceId);
        if (!selectedCellResource) {
          return [];
        }
        return [selectedCellResource.id];
      },
      [places, specialConditions],
    );

    const onAppointmentClick = useCallback((e: AppointmentClickEvent) => {
      e.cancel = true;
    }, []);

    const onAppointmentDoubleClick = useCallback(
      (e: AppointmentDblClickEvent) => {
        e.cancel = true;
        const appointmentData =
          e.appointmentData as AppointmentItem['data']['appointmentData'];
        if (appointmentData.isAnonymized || appointmentData.absenceType) {
          return;
        }
        const appointmentId = appointmentData.id as number;
        if (appointmentId) {
          forms.openVisitEditing(
            appointmentId,
            true,
            configuration.visitStartTimeInSeconds,
          );
        }
      },
      [configuration.visitStartTimeInSeconds],
    );

    const onAppointmentRendered = useCallback(
      (e: AppointmentRenderedEvent) => {
        if (!e.appointmentElement) {
          return;
        }
        const dataSource = new DXDS.DataSource([
          (e.appointmentData as any).data,
        ]);
        const fittingRule = (colorSettingsQuery.data?.rules ?? []).find(
          (rule) => {
            const parsedFilterExpressions = (rule as any)
              .parsedFilterExpression as any;

            return isAnyObjectInDataSourceSuitFilter(
              dataSource,
              parsedFilterExpressions,
            );
          },
        );
        const color = getBGColor(
          fittingRule?.color ?? 'transparent',
          isNightTheme,
        );
        dataSource.dispose();
        e.appointmentElement.style.backgroundColor = color;
        e.appointmentElement.style.color = getFontColor(color);

        if (hasExcludedDays && excludedDays) {
          const cellDate = new Date(e?.appointmentData.startDate ?? '');
          const cellDay = cellDate.getDay();

          const cellDayIndex = convertDaysToIndex(
            [cellDay],
            firstDayOfWeekIndexConfiguration,
          )[0];
          const excludedIndexes = convertDaysToIndex(
            excludedDays.filter((x) => x > firstDayOfWeekIndex),
            firstDayOfWeekIndexConfiguration,
          );
          const endDayOfWeek = endOfWeek(currentDate, {
            weekStartsOn: firstDayOfWeekIndexConfiguration,
          });

          const skippedDaysBefore =
            excludedIndexes.filter((x) => x < cellDayIndex).length ?? 0;

          // We need to checking for the last day of week
          // because we need to understand is this day from previous week (we should not move it) or not
          if (
            skippedDaysBefore > 0 &&
            (cellDay !== endDayOfWeek.getDay() ||
              isSameDay(endDayOfWeek, cellDate))
          ) {
            const scheduler = e.appointmentElement.closest('.dx-scheduler');
            const cell = scheduler?.querySelector(
              '.dx-scheduler-date-table td',
            ) as HTMLElement;

            const matrix = new DOMMatrix(e.appointmentElement.style.transform);
            matrix.m41 -= skippedDaysBefore * cell.offsetWidth;
            e.appointmentElement.style.transform = matrix.toString();
          }
        }

        if (currentView === 'month') {
          e.appointmentElement.style.opacity =
            new Date(e?.appointmentData?.endDate ?? '') <
              startOfMonth(currentDate) ||
            new Date(e?.appointmentData?.startDate ?? '') >
              endOfMonth(currentDate)
              ? '0.5'
              : '1';
        }
      },
      [
        colorSettingsQuery.data,
        isNightTheme,
        currentDate,
        currentView,
        excludedDays,
        hasExcludedDays,
        firstDayOfWeekIndexConfiguration,
      ],
    );

    const renderDateCell = useCallback(
      (cellData: { date: Date }) => {
        const cellDate = cellData.date;
        if (props.currentView === 'month') {
          return (
            <div className={styles.dateCellContainer}>
              {localFormat(cellDate, 'EEE')}
            </div>
          );
        }

        if (excludedDays?.includes(cellDate.getDay())) {
          return;
        }

        const holidays: HolidaysAndAnonimized[] = (
          props.holidays[format(startOfDay(cellDate), 'yyyy-MM-dd')] ?? []
        ).sort((a, b) =>
          a.title.toLowerCase() > b.title.toLowerCase()
            ? 1
            : b.title.toLowerCase() > a.title.toLowerCase()
              ? -1
              : 0,
        );
        const notes: NoteDto[] = props.notes[format(cellDate, 'yyyy-MM-dd')];
        const today = startOfDay(new Date());

        return (
          <div
            className={clsx(
              styles.dateCellContainer,
              isSameDay(cellDate, today) && styles.todayCell,
              holidays.length > 0 && styles.holidayCell,
            )}
          >
            <div
              className={clsx(
                styles.dateCellName,
                isSameDay(cellDate, today) && styles.todayCellContainer,
                isSameDay(cellDate, today) && styles.currentDateCellName,
              )}
            >
              {localFormat(cellDate, 'EEE d')}
            </div>
            <div className={styles.dateCellFooter}>
              {notes?.length && (
                <div
                  className={styles.dateCellNoteContainer}
                  onMouseOver={(e) => {
                    setPopupNotes({
                      date: cellDate,
                      notes,
                    });
                    bindHoverRef.onMouseOver(e);
                  }}
                  onMouseLeave={(e) => {
                    bindHoverRef.onMouseLeave(e);
                  }}
                  data-test-id="date-cell-note-container"
                >
                  <NoteIconComponent notes={notes} />
                  <div className={styles.noteTextContainer}>
                    {notes[0].text}
                  </div>
                </div>
              )}
              {holidays.length > 0 && (
                <div className={clsx(styles.nonMonthHoliday)}>
                  <HolidayIcon className={calendarStyles.holidayIcon} />
                  <span className={styles.holidayTitle}>
                    {holidays[0].title}
                  </span>
                </div>
              )}
            </div>
          </div>
        );
      },
      [props.currentView, props.notes, props.holidays],
    );

    const renderResourceCell = useCallback(
      (model: { id: number; text: string; data?: any }) => {
        const isCurrentRoom =
          workstationSettings?.isMapWorkstationToRoomEnabled &&
          model.id === workstationSettings?.roomId;
        return (
          <div
            is-room-active={`${isCurrentRoom}`}
            key={model.id}
            className={styles.resource}
          >
            <div
              className={clsx(
                styles.resourceName,
                isCurrentRoom && styles.currentRoom,
              )}
            >
              <span>{model.text}</span>
              {isCurrentRoom && <CheckedIcon />}
            </div>
            {model.data?.additionalText && (
              <div className={styles.resourceAdditionalText}>
                <span>{model.data.additionalText}</span>
              </div>
            )}
          </div>
        );
      },
      [
        workstationSettings?.roomId,
        workstationSettings?.isMapWorkstationToRoomEnabled,
      ],
    );

    const visitType = useMemo(() => {
      return visitTypes.find((x) => x.id === configuration.defaultVisitTypeId);
    }, [configuration, visitTypes]);

    const onDataCellClick = useEventCallback(
      (
        patientId: number | undefined,
        cell: Omit<CalendarCellDropTarget, 'type'>,
      ) => {
        const start = new Date(cell.startDate.getTime());
        let end = new Date(cell.endDate.getTime());
        if (
          currentView === 'timelineWeek' ||
          currentView === 'month' ||
          currentView === 'timelineMonth'
        ) {
          // for timeline week and month view where cell takes the whole day (without specific time), we use start time of visit type
          // or use configuration settings if visit type start time is not set
          const visitStartInSeconds =
            visitType?.specificStartTimeInSecondsSinceMidnight ??
            configuration.visitStartTimeInSeconds;
          const durationInSeconds =
            visitType?.defaultDurationInSeconds ??
            defaultVisitDurationInSeconds;
          start.setSeconds(visitStartInSeconds);
          end.setHours(visitStartInSeconds + durationInSeconds);
          if (end < start) {
            end = addDays(end, 1);
          }
        }
        forms.openVisitCreating(
          {
            start: start,
            end: end,
          },
          getResourcesToCreateNewVisit(cell.resource!),
          patientId,
          undefined,
          configuration.defaultVisitTypeId,
          configuration.timeSlotSearchTimeStepInMinutes,
          currentView,
          configuration.visitStartTimeInSeconds,
        );
      },
    );
    const appointments = useMemo(() => {
      let appointmentsPropsLocal = appointmentsProps;
      if (props.currentView === 'month') {
        const reducedPerDay = appointmentsPropsLocal
          .sort((a, b) => (a.startDate! > b.startDate! ? 1 : -1))
          .reduce(
            (result, current) => {
              const currentAppointment = { ...current };

              const start = startOfDay(currentAppointment.startDate as any);
              const key = start.toISOString();

              if (!result[key]) {
                result[key] = [];
              }

              if (result[key].length < maxVisitsPerMonthCell) {
                currentAppointment.endDate = endOfDay(
                  currentAppointment.startDate as any,
                );
                result[key].push(currentAppointment);
              }

              return result;
            },
            {} as Record<string, Appointment[]>,
          );

        appointmentsPropsLocal = [
          ...new Set(
            Object.values(reducedPerDay).reduce(
              (prev, current) => [...prev, ...current],
              [],
            ),
          ),
        ];
      }

      if (!hasExcludedDays || !excludedDays) return appointmentsPropsLocal;

      return correctAppointmentsAccordingToExcludedDays(
        appointmentsPropsLocal,
        excludedDays,
      );
    }, [appointmentsProps, excludedDays, hasExcludedDays, props.currentView]);

    const renderDataCell = useCallback(
      (
        cellProps: {
          startDate: Date;
          endDate: Date;
          allDay?: boolean;
          groups: { resources: number };
        },
        index: number,
      ) => {
        const currentResourceId =
          cellProps.groups?.resources ??
          Object.values(cellProps.groups ?? {})[0];

        const holidays: HolidaysAndAnonimized[] =
          props.holidays[
            format(startOfDay(cellProps.startDate), 'yyyy-MM-dd')
          ] ?? [];

        const notes: NoteDto[] =
          (props.currentView === 'month'
            ? props.notes[format(cellProps.startDate, 'yyyy-MM-dd')]
            : undefined) ?? [];
        const appointmentsLocal = appointmentsProps.filter(
          (x) =>
            !x.absenceType &&
            x.startDate! < cellProps.endDate &&
            x.endDate! > cellProps.startDate &&
            (currentResourceId !== undefined
              ? x?.resources?.includes(currentResourceId)
              : true),
        );

        const appointmentsStartBeforeCellDate = appointmentsLocal.filter(
          (x) => x.startDate! < cellProps.startDate,
        );

        const displayWeekNumber =
          configuration.displayWeekNumber && index % 7 === 0;

        return (
          <div
            is-today={`${isSameDay(cellProps.startDate, new Date())}`}
            is-holiday={`${holidays.length > 0}`}
            onDoubleClick={() => {
              onDataCellClick(undefined, {
                startDate: cellProps.startDate,
                endDate: cellProps.endDate,
                resource: cellProps.groups?.resources ?? [],
              });
            }}
            className={clsx(
              styles.dataCell,
              holidays.length > 0 && styles.holidayContent,
              (isSameDay(cellProps.startDate, new Date()) ||
                (workstationSettings?.isMapWorkstationToRoomEnabled &&
                  cellProps.groups?.resources &&
                  workstationSettings?.roomId &&
                  cellProps.groups.resources === workstationSettings.roomId)) &&
                styles.currentItem,
            )}
          >
            <CalendarDateCellDropHandler
              startDate={cellProps.startDate}
              endDate={cellProps.endDate}
              resource={cellProps.groups?.resources}
            >
              <div className={styles.monthCellHeader}>
                {props.currentView === 'timelineMonth' && (
                  <div className={styles.monthCellHeaderContainer}>
                    {appointmentsLocal?.length >
                      maxVisitsPerTimelineMonthCell && (
                      <div
                        className={styles['timeline-month-total-count']}
                        data-test-id="month-view-total-visit-count"
                      >
                        <div
                          className={styles.monthCellTotalCountText}
                          onMouseOver={(e) => {
                            setPopupAppointments({
                              date: cellProps.startDate,
                              appointments: appointmentsLocal as any,
                            });
                            bindHoverVisitRef.onMouseOver(e);
                          }}
                          onMouseLeave={(e) => {
                            bindHoverVisitRef.onMouseLeave(e);
                          }}
                        >
                          {i18next.t(
                            'translation:calendar.month_view.total_count',
                            {
                              count: appointmentsLocal?.length,
                            },
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {props.currentView === 'month' && (
                  <div className={styles.monthCellHeaderContainer}>
                    {displayWeekNumber && (
                      <div
                        className={styles.weekNumber}
                        data-is-current-week={
                          getISOWeek(cellProps.startDate) ===
                          getISOWeek(new Date())
                        }
                        data-test-id="week-number-in-month-view"
                      >
                        {i18next.t(
                          'translation:calendar.resource_view.week_number',
                          {
                            number: getISOWeek(cellProps.startDate),
                          },
                        )}
                      </div>
                    )}
                    <div className={styles.monthCellHeader}>
                      <div>
                        {notes.length > 0 && (
                          <NoteIconComponent
                            onMouseOver={(e) => {
                              setPopupNotes({
                                date: cellProps.startDate,
                                notes,
                              });
                              bindHoverRef.onMouseOver(e);
                            }}
                            onMouseLeave={(e) => {
                              bindHoverRef.onMouseLeave(e);
                            }}
                            className={styles.noteIcon}
                            notes={notes}
                            data-test-id="month-view-note-icon"
                          />
                        )}
                      </div>
                      {holidays.length === 0 && (
                        <div className="dx-scheduler-date-table-cell-text">
                          {localFormat(cellProps.startDate, 'd')}
                        </div>
                      )}
                      {holidays.length > 0 && (
                        <div className={clsx(styles.holiday)}>
                          <span
                            className={clsx(
                              styles.dayOfMonthText,
                              isSameDay(cellProps.startDate, new Date()) &&
                                styles.todayHolidayOfMonth,
                            )}
                          >
                            {localFormat(cellProps.startDate, 'd')}
                          </span>
                          <HolidayIcon
                            className={calendarStyles.monthHolidayIcon}
                          />
                          <span className={styles.holidayTitle}>
                            {holidays[0].title}
                          </span>
                        </div>
                      )}
                    </div>
                    {(appointmentsLocal?.length > maxVisitsPerMonthCell ||
                      appointmentsStartBeforeCellDate.length > 0) && (
                      <div className={styles.monthCellTotalCount}>
                        <div
                          className={styles.monthCellTotalCountText}
                          onMouseOver={(e) => {
                            setPopupAppointments({
                              date: cellProps.startDate,
                              appointments: appointmentsLocal as any,
                            });
                            bindHoverVisitRef.onMouseOver(e);
                          }}
                          onMouseLeave={(e) => {
                            bindHoverVisitRef.onMouseLeave(e);
                          }}
                        >
                          {i18next.t(
                            'translation:calendar.month_view.total_count',
                            {
                              count: appointmentsLocal?.length,
                            },
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </CalendarDateCellDropHandler>
          </div>
        );
      },
      [
        props.currentView,
        props.holidays,
        props.notes,
        shownResourcesList,
        appointmentsProps,
        workstationSettings?.roomId,
        workstationSettings?.isMapWorkstationToRoomEnabled,
      ],
    );

    const setUpdatedResourcesOnDragAndDrop = useCallback(
      (visitWithResources: { resourceIds: number[] }, newResource: number) => {
        const resource = resources?.find((x) => x.id === newResource);
        if (!resource) {
          return null;
        }

        const oldResources = visitWithResources.resourceIds as number[];
        if (oldResources.includes(newResource)) {
          // visit was dropped on resource that is already chosen
          // need to update only dates
          return visitWithResources;
        }

        switch (resource.type) {
          case ResourceType.Places:
            // replace old room with new one
            const oldPlaceResource = resources?.find(
              (x) =>
                x.type === ResourceType.Places && oldResources?.includes(x.id),
            );
            visitWithResources.resourceIds = [
              ...oldResources?.filter(
                (id: number) => id !== oldPlaceResource?.id,
              ),
              newResource,
            ];
            break;
          case ResourceType.Doctors:
            // replace old doctor with new one
            const oldDoctorResource = resources?.find(
              (x) =>
                x.type === ResourceType.Doctors && oldResources?.includes(x.id),
            );
            visitWithResources.resourceIds = [
              ...oldResources?.filter(
                (id: number) => id !== oldDoctorResource?.id,
              ),
              newResource,
            ];
            break;
          case ResourceType.SpecialConditions:
            // add new special condition
            visitWithResources.resourceIds = [...oldResources, newResource];
            break;
        }

        return visitWithResources;
      },
      [resources],
    );

    const getNewDataOnDragAndDropWithinCalendar = useEventCallback(
      (event: AppointmentUpdatingEvent, calendarType: ViewType) => {
        const visitData = event.oldData.data as VisitListDto;
        const eventNewStartDate = event.newData.startDate as Date;
        const visitLengthInMills = differenceInMilliseconds(
          visitData.end,
          visitData.start,
        );

        // for timeline week view we need to calculate start time since start time of cell is start of day (midnight)
        // for other views target cell has time
        const startTimeSinceMidnight = differenceInMilliseconds(
          visitData.start,
          startOfDay(visitData.start),
        );
        const newStartDate =
          calendarType === 'timelineWeek'
            ? addMilliseconds(eventNewStartDate, startTimeSinceMidnight)
            : eventNewStartDate;

        const updateData = {
          resourceIds: visitData.resourceIds,
          start: newStartDate,
          end: addMilliseconds(newStartDate, visitLengthInMills),
          acceptConflict: false,
        };

        const newResourceId = event.newData.resources as number; // resource that user dropped visit on
        setUpdatedResourcesOnDragAndDrop(updateData, newResourceId);
        event.newData.resources = updateData.resourceIds;
        return updateData;
      },
    );

    /**
     * Handler of drag and drop when visit is from waiting list (outside calendar)
     */
    const onVisitDragAndDropFromWaitingList = useEventCallback(
      async (visitId: number, cell: Omit<CalendarCellDropTarget, 'type'>) => {
        const oldVisitData = await getOrCreateAppointmentModel(visitId);
        if (!oldVisitData) {
          return;
        }
        const visitData = oldVisitData.data as VisitDto;
        const visitLengthInMills = differenceInMilliseconds(
          visitData.end,
          visitData.start,
        );

        const startTimeSinceMidnight = differenceInMilliseconds(
          visitData.start,
          startOfDay(visitData.start),
        );
        const newStartDate =
          currentView === 'timelineWeek' ||
          currentView === 'month' ||
          currentView === 'timelineMonth'
            ? addMilliseconds(cell.startDate, startTimeSinceMidnight)
            : cell.startDate;

        const newVisitData = {
          resourceIds: oldVisitData.data.resourceIds as number[],
          start: newStartDate,
          end: addMilliseconds(newStartDate, visitLengthInMills),
          acceptConflict: false,
          doctorId: oldVisitData.doctorId as number | null,
          inWaitingList: false, // visit is being deleted from waiting list
        };
        setUpdatedResourcesOnDragAndDrop(newVisitData, cell.resource!);

        await updateVisit(visitId, newVisitData, oldVisitData, {
          newData: { data: visitData ?? {} },
          oldData: {},
          component: null!,
          element: null!,
        });
      },
    );

    /**
     * Handler of visits' drag and drop within calendar
     */
    const onVisitDragAndDropEndWithinCalendar = useEventCallback(
      async (event: AppointmentUpdatingEvent) => {
        if (!event.newData.id) return;

        const newData = getNewDataOnDragAndDropWithinCalendar(
          event,
          currentView,
        );
        if (!newData) {
          return;
        }
        const visitId = event.oldData.id as number;
        const visit = appointments.find((model) => model.id === visitId);
        if (!visit) {
          return;
        }
        // we need to store "old" visit model to rollback changes if user doesn't accept conflict (in the case when conflict appears)
        const oldVisitData = {
          ...visit,
          startDate: event.oldData.startDate,
          endDate: event.oldData.endDate,
          resources: event.oldData.resources,
        };

        await updateVisit(visitId, newData, oldVisitData, event);
      },
    );

    const onItemDropped = useEventCallback(
      async (
        itemId: number,
        itemType: DroppedItemType,
        cell: Omit<CalendarCellDropTarget, 'type'>,
      ) => {
        if (itemType === 'patient') {
          onDataCellClick(itemId, cell);
        } else if (itemType === 'visit') {
          await onVisitDragAndDropFromWaitingList(itemId, cell);
        }
      },
    );

    const onItemDragStart = useEventCallback(
      (e: { cancel: boolean; itemData: any }) => {
        if (props.currentView === 'month') {
          e.cancel = true;
          return;
        }
        if (e.itemData.isAnonymized || !!e.itemData.absenceType) {
          e.cancel = true;
        }
      },
    );

    // this is to prevent Bug #38552: Incorrect scaling
    // Resource View isn't resized properly when REDUCING the window size
    const { width } = useWindowSize();
    const [debouncedWidthValue] = useDebounce(width, 300);

    useEffect(
      function scrollToDateForTimelineMonthView() {
        if (props.currentView !== 'timelineMonth') {
          return;
        }

        const timer = setTimeout(() => {
          if (!schedulerRef.current) return;

          const instance = schedulerRef.current.instance();
          if (!instance) return;

          const dayOfMonth = currentDate.getDate();

          const element = instance.element();
          const cell = element.querySelector(
            '.dx-scheduler-header-panel-cell, .dx-scheduler-date-table-cell',
          ) as HTMLElement;

          if (cell) {
            const cellWidth = cell.getBoundingClientRect().width;

            const scrollToLeftX = (dayOfMonth - 1) * cellWidth;

            try {
              const scrollable = (
                instance as any
              )._workSpace?.getScrollable?.();

              if (scrollable && typeof scrollable.scrollTo === 'function') {
                scrollable.scrollTo({
                  left: scrollToLeftX,
                });
              }
            } catch (error) {
              instance.scrollTo(currentDate);
            }
          }
        }, 70);

        return () => clearTimeout(timer);
      },
      [currentDate, props.currentView],
    );

    useItemDroppedToCalendarCell(onItemDropped);
    // useMemo is used intentionally to prevent unnecessary calendar's re-rendering when option list is being opened
    return (
      <div
        className={clsx(
          styles.nightViewContainer,
          devExpressSchedulerStyles.fullWidthDevExpressScheduler,
        )}
        data-month-view={props.currentView === 'month'}
        data-week-numbers={configuration.displayWeekNumber}
        data-timeline-month-view={props.currentView === 'timelineMonth'}
      >
        <div
          className={clsx(styles.calendarContainer)}
          data-firstdayofweek={firstDayOfWeekIndex}
        >
          <EnableDebounceContext.Provider value={true}>
            <DXS.Scheduler
              key={`width-${currentView}-${debouncedWidthValue}`}
              ref={mergeRefs([schedulerRef, props.schedulerRef as any])}
              {...rest}
              currentView={currentView}
              className={clsx(
                hasExcludedDays &&
                  excludedDays &&
                  convertDaysToIndex(excludedDays, firstDayOfWeekIndex)?.map(
                    (x) => styles['exclude-day-' + x],
                  ),
                hasExcludedDays && styles.calendarWithExcludedDays,
                configuration.isResourceViewCalendar && styles.resourceView,
                !configuration.isResourceViewCalendar && styles.nonResourceView,
                props.className,
              )}
              dataSource={
                appointments as any /* remove ANY when `endDate` property in AppointmentModel and dxSchedulerAppointment will be typed identically*/
              }
              firstDayOfWeek={firstDayOfWeekIndex}
              showCurrentTimeIndicator={false}
              maxAppointmentsPerCell={'unlimited'}
              groups={groups}
              currentDate={
                currentView === 'month'
                  ? startOfMonth(currentDate)
                  : currentDate
              }
              onAppointmentRendered={onAppointmentRendered}
              onAppointmentClick={onAppointmentClick}
              onAppointmentDblClick={onAppointmentDoubleClick}
              dateCellRender={renderDateCell}
              resourceCellRender={renderResourceCell}
              editing={editingSettings}
              dataCellRender={renderDataCell}
              onAppointmentUpdating={onVisitDragAndDropEndWithinCalendar}
              onAppointmentFormOpening={cancel}
              width={'100%'}
              height={'100%'}
              // we need to manually set `allDay`, because Holidays could be longer than 1 day, but we don't want to move them to `All Day` panel
              allDayPanelMode={'allDay'}
              crossScrollingEnabled={true}
            >
              {shownResourcesList.length > 0 && (
                <DXS.Resource
                  fieldExpr={'resources'}
                  allowMultiple={false}
                  dataSource={shownResourcesList}
                  useColorAsDefault={true}
                />
              )}
              <DXS.View type="timelineWeek" cellDuration={1440} />
              <DXS.View
                type="timelineMonth"
                maxAppointmentsPerCell={maxVisitsPerTimelineMonthCell}
                cellDuration={1440}
              />
              <DXS.View type="timelineDay" cellDuration={120} />
              <DXS.View
                type="month"
                maxAppointmentsPerCell={maxVisitsPerMonthCell + 1}
                startDayHour={0}
                endDayHour={24}
                appointmentTooltipComponent={AppointmentPopupAdapter}
              />
              <DXS.View type="week" cellDuration={60} />
              <DXS.View type="day" cellDuration={60} />
              <DXS.AppointmentDragging onDragStart={onItemDragStart} />
            </DXS.Scheduler>
          </EnableDebounceContext.Provider>
          {popupNotes && (
            <AppPopper {...bindPopper(popupState)} delay={500}>
              <NotePopup
                notes={popupNotes.notes}
                handleClose={popupState.close}
              />
            </AppPopper>
          )}
          {popupAppointments && (
            <AppPopper {...bindPopper(popupVisitState)} delay={800}>
              <MonthAppointmentPopup
                appointments={popupAppointments.appointments}
                date={popupAppointments.date}
                handleClose={popupVisitState.close}
              />
            </AppPopper>
          )}
        </div>
      </div>
    );
  },
);
function cancel(e: Cancelable) {
  e.cancel = true;
}

export const AppointmentPopupAdapter = (props: any) => {
  const item = props as AppointmentItem;
  const { doctor, room, specialConditions } = useAppointmentResourceDetails(
    item.data.appointmentData.data,
  );
  const patient = item.data.appointmentData.patient;
  const subtenantCode = item.data.appointmentData.subtenantCode;
  const measurements = useVisitDeviceMeasurements(
    item.data.appointmentData.data.id,
  );

  return (
    <AppointmentPopup
      appointmentData={item.data.appointmentData.data}
      patient={patient}
      subtenantCode={subtenantCode}
      doctor={doctor}
      room={room}
      hideDate={true}
      resources={specialConditions}
      measurements={measurements}
    />
  );
};
